import {
  DataGrid,
  GridColDef,
  getGridStringOperators,
  getGridSingleSelectOperators,
} from "@mui/x-data-grid";
import React, { FC, useState } from "react";

interface Scan {
  id: string;
  createdAt: string;
  updatedAt?: string;
  type: string;
  method: string;
  subscriber: {
    firstName: string;
    lastName: string;
  };
  firstresponder: {
    firstName: string;
    lastName: string;
  };
}

interface FilteringInterface {
  order: {
    by: string;
    type: string;
  };
  filter: {
    column: string | null;
    operator: string;
    value: string | null;
  };
  or?: {
    column: string;
    operator: string;
    value: string;
  };
}

const customStringOperators = getGridStringOperators().filter(({ value }) =>
  ["contains", "equals", "startsWith", "endsWith"].includes(value)
);

const customSingleSelectOperators = getGridSingleSelectOperators().filter(
  ({ value }) => ["is"].includes(value)
);

const LastScanDateTime = ({
  row,
  type,
}: {
  row: Scan;
  type: "date" | "time";
}) => {
  const date = row?.createdAt ? new Date(row.createdAt) : new Date();

  return (
    <p>
      {type === "date" ? (
        <>{date.toLocaleDateString("en-US")}</>
      ) : (
        <>
          {date.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          })}
        </>
      )}
    </p>
  );
};

const columns: GridColDef<Scan>[] = [
  {
    field: "createdAt",
    headerName: "Date of Scan",
    flex: 1,
    disableColumnMenu: true,
    renderCell: (params) => (
      <LastScanDateTime row={params.row as Scan} type="date" />
    ),
    valueGetter: (params) => {
      return params.row.createdAt;
    },
  },
  {
    field: "createdAtTime",
    headerName: "Time of Scan",
    flex: 1,
    disableColumnMenu: true,
    renderCell: (params) => (
      <LastScanDateTime row={params.row as Scan} type="time" />
    ),
    valueGetter: (params) => {
      return params.row.createdAt;
    },
  },
  {
    field: "firstresponder.firstName",
    headerName: "First Responder Name",
    flex: 1,
    filterOperators: customStringOperators,
    valueGetter: (params) =>
      `${params.row.firstresponder?.firstName || ""} ${
        params.row.firstresponder?.lastName || ""
      }`,
  },
  {
    field: "subscriber.firstName",
    headerName: "Subscriber Name",
    flex: 1,
    filterOperators: customStringOperators,
    valueGetter: (params) =>
      `${params.row.subscriber.firstName} ${
        params.row.subscriber.lastName || ""
      }`,
  },
  {
    field: "type",
    headerName: "Page Viewed",
    flex: 1,
    filterOperators: customSingleSelectOperators,
    type: "singleSelect",
    valueOptions: [
      { value: "Family Members", label: "Members Page" },
      { value: "Full Profile", label: "Full Profile" },
    ],
    valueGetter: (params) =>
      params.value === "Family Members" ? "Members Page" : "Full Profile",
  },
  {
    field: "method",
    headerName: "Type of Scan",
    flex: 1,
    filterOperators: customSingleSelectOperators,
    type: "singleSelect",
    valueOptions: [
      { value: "MMHID", label: "MMH ID" },
      { value: "biometric", label: "Biometrics" },
      { value: "QR", label: "QR" },
    ],
    valueGetter: (params) => {
      const methods: any = {
        MMHID: "MMH ID",
        biometric: "Biometrics",
        QR: "QR",
      };
      return methods?.[params.value] ?? "N/A";
    },
  },
];

interface Props {
  scans?: Scan[];
  loading: boolean;
  pagination?: {
    page: number;
    pageSize: number;
    total: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
  };
  sorting?: {
    sortBy: string;
    sortOrder: "ASC" | "DESC";
    onSortChange: (sortBy: string, sortOrder: "ASC" | "DESC") => void;
  };
  filtering?: {
    filters: FilteringInterface;
    setFiltering: React.Dispatch<React.SetStateAction<FilteringInterface>>;
    changePage: (page: number) => void;
  };
}

const AgencyScansTable: FC<Props> = ({
  scans,
  loading,
  pagination,
  sorting,
  filtering,
}) => {
  const [localPageSize, setLocalPageSize] = useState(10);

  const pageSize = pagination?.pageSize || localPageSize;
  const setPageSize = pagination?.onPageSizeChange || setLocalPageSize;

  // Map frontend column fields to backend sort fields
  const mapSortField = (field: string): string => {
    const fieldMap: { [key: string]: string } = {
      createdAt: "createdAt",
      createdAtTime: "createdAt",
      "firstresponder.firstName": "firstresponder.firstName",
      "subscriber.firstName": "subscriber.firstName",
      type: "type",
      method: "method",
    };
    return fieldMap[field] || "createdAt";
  };

  const handleSortModelChange = (model: any) => {
    if (model.length > 0 && sorting?.onSortChange) {
      const field = model[0].field;
      const order = model[0].sort?.toUpperCase() || "ASC";
      const mappedField = mapSortField(field);
      sorting.onSortChange(mappedField, order as "ASC" | "DESC");
    }
  };

  const handleFilterModelChange = (model: any) => {
    if (!filtering) return;

    if (model?.items?.[0]?.value) {
      filtering.changePage(0);
      const operator: any = model?.items?.[0]?.operatorValue;
      const columnField = model?.items?.[0]?.columnField;
      const value = model?.items?.[0]?.value;

      // Handle subscriber and first responder name filtering (search both first and last name)
      if (
        columnField === "subscriber.firstName" ||
        columnField === "firstresponder.firstName"
      ) {
        const baseColumn = columnField.split(".")[0]; // 'subscriber' or 'firstresponder'
        filtering.setFiltering((prev: FilteringInterface) => ({
          ...prev,
          filter: {
            column: `${baseColumn}.firstName`,
            operator,
            value,
          },
          or: {
            column: `${baseColumn}.lastName`,
            operator,
            value,
          },
        }));
        return;
      }

      // Handle date filtering
      if (columnField === "createdAt") {
        let date: string;
        if (value.length > 8) {
          date = new Date(value).toJSON().split("T")[0];
        } else if (value.length === 5) {
          const dateObj = new Date(value);
          date = `${(dateObj.getMonth() + 1).toLocaleString("en-US", {
            minimumIntegerDigits: 2,
            useGrouping: false,
          })}-${dateObj.getDate().toLocaleString("en-US", {
            minimumIntegerDigits: 2,
            useGrouping: false,
          })}`;
        } else if (value.length <= 4) {
          date = value;
        } else {
          date = value;
        }

        filtering.setFiltering((prev: FilteringInterface) => ({
          ...prev,
          filter: {
            column: "createdAt",
            operator,
            value: `"${date}"`,
          },
        }));
        return;
      }

      // Handle other column filters
      filtering.setFiltering((prev: FilteringInterface) => ({
        ...prev,
        filter: {
          column: columnField,
          operator,
          value,
        },
      }));
    } else {
      // Clear filters
      filtering.setFiltering((prev: FilteringInterface) => ({
        ...prev,
        filter: {
          column: null,
          operator: "contains",
          value: null,
        },
      }));
    }
  };

  const dataGridProps = pagination
    ? {
        // Server-side pagination
        paginationMode: "server" as const,
        rowCount: pagination.total,
        page: pagination.page - 1, // DataGrid uses 0-based pagination
        onPageChange: (newPage: number) => pagination.onPageChange(newPage + 1), // Convert back to 1-based
        pageSize,
        onPageSizeChange: setPageSize,
        rowsPerPageOptions: [10, 20, 50],
      }
    : {
        // Client-side pagination
        pageSize,
        onPageSizeChange: setPageSize,
        rowsPerPageOptions: [10, 20, 50],
      };

  return (
    <DataGrid
      sx={{
        color: "black",
        border: 0,
        ".MuiDataGrid-columnSeparator": {
          display: "none",
        },
        ".MuiDataGrid-row": {
          borderRadius: "4px",
          backgroundColor: "#FFFFFF",
        },
        ".MuiDataGrid-columnHeaders": {
          borderRadius: "4px",
          backgroundColor: "#E0E2E9",
        },
        "& .MuiCheckbox-root": {
          color: "#000094",
        },
        ".MuiDataGrid-columnHeaderTitle": {
          fontWeight: 400,
        },
      }}
      loading={loading}
      rows={scans ?? []}
      columns={columns}
      disableColumnMenu={!filtering}
      disableColumnSelector
      autoHeight
      pagination
      disableSelectionOnClick
      sortingMode={sorting ? "server" : "client"}
      onSortModelChange={sorting ? handleSortModelChange : undefined}
      filterMode={filtering ? "server" : undefined}
      onFilterModelChange={filtering ? handleFilterModelChange : undefined}
      {...dataGridProps}
    />
  );
};

export default AgencyScansTable;
